import { create } from 'zustand';
import type { Theme, DriveRole } from '../types';
import type { SessionUser, SessionOrg } from '../services/auth';

// Only non-sensitive UI preferences are persisted client-side — no token, no
// identity. Session identity is sourced fresh from the platform's cookie-
// authenticated /v1/me on every load (see SessionContext + RootGate), the
// same split sk-catalog-frontend uses (Token_Handler stores only display
// data, never a JWT).
const UI_PREFS_KEY = 'skdrive.ui';

interface UiPrefs {
  theme: Theme;
  viewMode: 'workspace' | 'personal';
}

function loadUiPrefs(): Partial<UiPrefs> {
  if (typeof localStorage === 'undefined') return {};
  try {
    return JSON.parse(localStorage.getItem(UI_PREFS_KEY) || 'null') ?? {};
  } catch {
    return {};
  }
}

function saveUiPrefs(prefs: UiPrefs) {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(UI_PREFS_KEY, JSON.stringify(prefs)); } catch {}
}

export type Toast = { id: string; message: string; tone?: 'success' | 'danger' | 'warn' | 'info' };

interface AppState {
  // Session identity — populated by RootGate from the platform's /v1/me.
  isAuthenticated: boolean;
  userId: string;
  orgId: string;
  email: string;
  firstName: string;
  displayName: string;
  driveRole: DriveRole;

  // UI
  theme: Theme;
  viewMode: 'workspace' | 'personal';
  isAdmin: boolean;
  toasts: Toast[];

  // Actions
  setSession: (user: SessionUser | null, org: SessionOrg | null, role: string | null) => void;
  clearSession: () => void;
  setDriveRole: (role: DriveRole) => void;
  toggleTheme: () => void;
  setViewMode: (mode: 'workspace' | 'personal') => void;
  setIsAdmin: (v: boolean) => void;
  toast: (message: string, tone?: Toast['tone']) => void;
  dismissToast: (id: string) => void;
}

const uiPrefs = loadUiPrefs();

const PLATFORM_ADMIN_ROLES = new Set(['org_admin', 'admin', 'platform_admin']);

export const useAppStore = create<AppState>((set, get) => ({
  isAuthenticated: false,
  userId: '',
  orgId: '',
  email: '',
  firstName: '',
  displayName: '',
  driveRole: 'VIEWER',
  theme: uiPrefs.theme ?? 'light',
  viewMode: uiPrefs.viewMode ?? 'workspace',
  isAdmin: false,
  toasts: [],

  setSession: (user, org, role) => {
    const fullName = `${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim();
    set({
      isAuthenticated: !!(user && org),
      userId: user?.id ?? '',
      orgId: org?.id ?? '',
      email: user?.email ?? '',
      firstName: user?.first_name ?? '',
      displayName: fullName || user?.first_name || '',
      isAdmin: !!role && PLATFORM_ADMIN_ROLES.has(role),
    });
  },

  clearSession: () => {
    set({ isAuthenticated: false, userId: '', orgId: '', email: '', firstName: '', displayName: '', isAdmin: false });
  },

  setDriveRole: (role) => set({ driveRole: role }),

  toggleTheme: () => {
    const next = get().theme === 'light' ? 'dark' : 'light';
    set({ theme: next });
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', next);
    }
    saveUiPrefs({ theme: next, viewMode: get().viewMode });
  },

  setViewMode: (mode) => {
    set({ viewMode: mode });
    saveUiPrefs({ theme: get().theme, viewMode: mode });
  },

  setIsAdmin: (v) => set({ isAdmin: v }),

  toast: (message, tone = 'success') => {
    const id = `t_${Math.random().toString(36).slice(2)}`;
    set(s => ({ toasts: [...s.toasts, { id, message, tone }] }));
    setTimeout(() => get().dismissToast(id), 3200);
  },

  dismissToast: (id) => set(s => ({ toasts: s.toasts.filter(t => t.id !== id) })),
}));
